import { ROUTES } from "../../constants/routes";
import { Component, inject } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Navbar } from "../../components/navbar/navbar";
import { Breadcrumb } from "../../components/breadcrumb/breadcrumb";
import { Footer } from "../../components/footer/footer";
import { SeoService } from "../../services/seo.service";
import { SITE } from "../../constants/site";
import {
  breadcrumbSchema,
  serviceSchema,
  softwareApplicationSchema,
  webPageSchema
} from "../../helpers/structured-data";

@Component({
  selector: "app-developers",
  imports: [RouterLink, Breadcrumb, Navbar, Footer],
  templateUrl: "./developers.html",
  styleUrl: "./developers.scss"
})
export class Developers {
  readonly routes = ROUTES;
  private readonly seo = inject(SeoService);
  readonly site = SITE;

  requestSample = `curl -X POST https://api.verifydeceased.com/v1/verify \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "first_name": "Jane",
    "last_name": "Doe",
    "date_of_birth": "1948-03-12",
    "ssn": "123-45-6789",
    "address": "123 Main St, Alta Loma, CA 91701"
  }'`;

  responseSample = `{
  "status": "match",
  "confidence": 0.98,
  "record": {
    "first_name": "Jane",
    "last_name": "Doe",
    "date_of_death": "2024-11-03",
    "age_at_death": 76,
    "last_known_address": "123 Main St, Alta Loma, CA 91701",
    "state": "CA"
  },
  "request_id": "req_9f2c1a7b"
}`;

  batchSample = `curl -X POST https://api.verifydeceased.com/v1/verify/batch \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "records": [
      { "first_name": "Jane", "last_name": "Doe", "date_of_birth": "1948-03-12" },
      { "first_name": "John", "last_name": "Smith", "date_of_birth": "1952-07-22" }
    ]
  }'`;

  endpoints = [
    { method: "POST", path: "/v1/verify", description: "Verify a single identity in real time." },
    { method: "POST", path: "/v1/verify/batch", description: "Verify up to 1,000 identities per request." },
    { method: "GET", path: "/v1/jobs/{id}", description: "Check the status of an uploaded batch job." },
    { method: "GET", path: "/v1/results/{id}", description: "Download the results of a completed job." }
  ];

  fields = [
    { name: "first_name", type: "string", required: "Required", note: "Legal first name" },
    { name: "last_name", type: "string", required: "Required", note: "Legal last name" },
    { name: "date_of_birth", type: "string", required: "Recommended", note: "Format YYYY-MM-DD" },
    { name: "ssn", type: "string", required: "Optional", note: "Improves match confidence" },
    { name: "address", type: "string", required: "Optional", note: "Last known address" }
  ];

  statuses = [
    { code: "match", note: "A confirmed deceased record was found." },
    { code: "no_match", note: "No deceased record was found for this identity." },
    { code: "review", note: "A partial match was found and needs manual review." }
  ];

  ngOnInit(): void {
    this.seo.apply({
      title: "Death Verification API Documentation",
      description:
        "Integrate deceased verification with a REST API. Verify one identity in real time or submit batches, with clear JSON requests, responses and authentication.",
      path: "/developers",
      keywords:
        "death verification API, deceased verification REST API, death data API, verify deceased API integration, death screening developer docs",
      structuredData: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Developers", path: "/developers" }
        ]),
        webPageSchema({
          name: "VerifyDeceased API documentation",
          description:
            "REST API reference for real time and batch deceased consumer verification.",
          path: "/developers",
          type: "TechArticle"
        }),
        softwareApplicationSchema(),
        serviceSchema()
      ]
    });
  }
}
